import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Card, DataTable, Input, Pagination, Select, Tabs } from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { relative } from '../../lib/format.js';
import { CATEGORY_LABELS, KIND_LABELS, StatusBadge, STATUS_LABELS } from './supportShared.jsx';

/**
 * Questions and problems people raise from their profile in either app.
 *
 * Open ones come first, longest-waiting at the top, so the list reads as a
 * queue. The tabs carry live counts; a row opens the conversation.
 */
export default function SupportPage() {
  const navigate = useNavigate();
  const list = useListQuery({
    key: 'admin-support',
    fetcher: api.support.list,
    initialFilters: { status: 'open', category: '', kind: '' },
    searchable: true,
  });
  const counts = useQuery({ queryKey: ['admin-support-summary'], queryFn: api.support.summary, staleTime: 15_000 });

  const tabs = [
    ...Object.entries(STATUS_LABELS).map(([value, label]) => ({
      value,
      label: counts.data ? `${label} (${counts.data[value] ?? 0})` : label,
    })),
    { value: '', label: 'All' },
  ];

  const columns = [
    {
      key: 'subject',
      header: 'Query',
      render: (query) => (
        <div>
          <div className="table__primary">{query.subject}</div>
          <div className="table__muted">{query.preview}</div>
        </div>
      ),
    },
    {
      key: 'who',
      header: 'From',
      render: (query) => (
        <div>
          <div>{query.user?.name || 'Unnamed'}</div>
          <div className="table__muted">{KIND_LABELS[query.user?.kind]}</div>
        </div>
      ),
    },
    { key: 'category', header: 'Topic', render: (query) => CATEGORY_LABELS[query.category] || query.category },
    {
      key: 'status',
      header: 'Status',
      render: (query) => (
        <div>
          <StatusBadge status={query.status} />
          {query.lastMessageFrom === 'user' && query.status !== 'closed' ? (
            <div className="table__muted">Waiting on you</div>
          ) : null}
        </div>
      ),
    },
    { key: 'when', header: 'Last message', render: (query) => <span className="table__muted">{relative(query.lastMessageAt)}</span> },
  ];

  return (
    <Page title="Support" subtitle="Queries raised from the rental and broker apps. Reply here; the person is notified and sees it in their profile.">
      <Card bodyless>
        <div className="card__body">
          <Tabs tabs={tabs} value={list.filters.status} onChange={(value) => list.setFilter('status', value)} label="Query status" />
          <div className="toolbar" style={{ marginTop: 12 }}>
            <Input
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
              placeholder="Search title, name or phone"
              aria-label="Search queries"
            />
            <Select value={list.filters.kind} onChange={(event) => list.setFilter('kind', event.target.value)} aria-label="Which app">
              <option value="">Both apps</option>
              <option value="customer">Tenant / owner app</option>
              <option value="broker">Broker app</option>
            </Select>
            <Select value={list.filters.category} onChange={(event) => list.setFilter('category', event.target.value)} aria-label="Topic">
              <option value="">Any topic</option>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={list.rows}
          query={list.query}
          rowKey={(query) => query.id}
          onRowClick={(query) => navigate(`/support/${query.id}`)}
          empty={{ title: 'No queries here', body: 'Nothing matches these filters. New queries arrive when someone raises one from their profile.' }}
        />
        <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
      </Card>
    </Page>
  );
}
